import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Modal,
  Radio,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import { Link } from "react-router-dom";
import { mergeApi, errMsg } from "../institutionMerge.api";
import { fmtDate } from "../institutionMerge.constants";
import {
  After,
  ArrayDiff,
  Before,
  HIDDEN_FIELDS,
  ModelTag,
  OPS,
  PHASES,
  ProvenanceTag,
  StateTags,
  afterCol,
  beforeCol,
  fieldLabel,
  modelLabel,
  recordLink,
  same,
  show,
} from "./recordDisplay";

const { Text } = Typography;

const CHANGED_BG = "#fffbe6";

/** A special state shown instead of a value: « n'existait pas », « supprimé·e par la fusion », « n'existe plus ». */
const Special = ({ children }) => (
  <Text type="secondary" italic>
    {children}
  </Text>
);

const journalRowText = r => {
  const bits = [PHASES[r.phase] || r.phase];
  if (r.field) bits.push(r.field);
  else if (r.op) bits.push(OPS[r.op] || r.op);
  return `#${r.seq} (${bits.filter(Boolean).join(" · ")})`;
};

/**
 * The complete card of one document the merge touched — EVERY field, with what it held
 * before the merge, right after it, and now; for a source institution a 4th column says what
 * the resulting institution holds for the same field and where that value came from.
 * Props: mergeId, model, documentId, open, onClose, label? (shown until the card loads),
 * onOpenJournal?(seq) — when given, the journal rows in the footer become links.
 */
export function FullRecordModal({
  mergeId,
  model,
  documentId,
  open,
  onClose,
  label,
  onOpenJournal,
}) {
  const [s, setS] = useState({ loading: false, error: null, data: null });
  const [mode, setMode] = useState("changed");
  const load = useCallback(async () => {
    if (!mergeId || !model || !documentId) return;
    setS({ loading: true, error: null, data: null });
    try {
      const data = await mergeApi.document(mergeId, model, documentId);
      setS({ loading: false, error: null, data });
      const n = (data.fields || []).filter(f => f.changed).length;
      setMode(n ? "changed" : "all");
    } catch (e) {
      setS({ loading: false, error: errMsg(e), data: null });
    }
  }, [mergeId, model, documentId]);
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const d = s.data;
  const fields = (d ? d.fields || [] : [])
    .filter(f => !HIDDEN_FIELDS.includes(f.path))
    .map(f => ({ ...f, key: f.path }));
  // changed rows first, the rest in the server's (schema) order
  const sorted = [
    ...fields.filter(f => f.changed),
    ...fields.filter(f => !f.changed),
  ];
  const changedCount = fields.filter(f => f.changed).length;
  const rows = mode === "changed" ? sorted.filter(f => f.changed) : sorted;
  const labels = (d && d.labels) || {};
  const target = d && d.target;
  const title = `${(d && d.label) ||
    label ||
    modelLabel(model)} — fiche complète`;
  const link = d && d.exists ? recordLink(d.model, d.documentId) : null;

  const columns = [
    {
      title: "Champ",
      dataIndex: "path",
      width: 200,
      render: (p, f) => (
        <Space size={4} wrap>
          <Text strong={f.changed}>{f.label || fieldLabel(p)}</Text>
          {f.changed && (
            <Tag color="gold" style={{ marginRight: 0 }}>
              modifié
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Avant",
      dataIndex: "before",
      ...beforeCol,
      render: (v, f) =>
        d.createdByMerge ? (
          <Special>n'existait pas</Special>
        ) : (
          <Before value={v} changed={!!f.changed} labels={labels} />
        ),
    },
    {
      title: "Après (fusion)",
      dataIndex: "after",
      ...afterCol,
      render: (v, f) =>
        d.deletedByMerge ? (
          <Special>supprimé·e par la fusion</Special>
        ) : (
          <Space direction="vertical" size={0}>
            <After value={v} changed={!!f.changed} labels={labels} />
            {f.changed && (Array.isArray(v) || Array.isArray(f.before)) && (
              <ArrayDiff before={f.before} after={v} labels={labels} />
            )}
          </Space>
        ),
    },
    {
      title: "Actuellement",
      dataIndex: "current",
      render: (v, f) => {
        if (d.exists === false) return <Special>n'existe plus</Special>;
        const sinceMerge = !d.deletedByMerge && !same(v, f.after);
        return (
          <Space size={4} wrap>
            <Text>{show(v, labels)}</Text>
            {sinceMerge && (
              <Tag style={{ marginRight: 0 }}>modifié depuis la fusion</Tag>
            )}
          </Space>
        );
      },
    },
    ...(target
      ? [
          {
            title: (
              <span>
                → Institution résultante
                <br />
                <Text type="secondary" style={{ fontWeight: "normal" }}>
                  {target.label}
                </Text>
              </span>
            ),
            key: "target",
            ...afterCol,
            render: (_, f) => {
              const tf = target.fields || {};
              const prov = (target.provenance || {})[f.path];
              const has = Object.prototype.hasOwnProperty.call(tf, f.path);
              if (!has) return <Text type="secondary">—</Text>;
              const differs = !same(tf[f.path], f.before);
              return (
                <Space size={4} wrap>
                  <After value={tf[f.path]} changed={differs} labels={labels} />
                  <ProvenanceTag value={prov} />
                </Space>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={target ? 1180 : 980}
      title={title}
      destroyOnClose
    >
      {s.loading && (
        <div style={{ textAlign: "center", padding: 32 }}>
          <Spin tip="Chargement de la fiche complète…">
            <div style={{ height: 40 }} />
          </Spin>
        </div>
      )}
      {s.error && (
        <Alert
          type="error"
          showIcon
          message="Fiche complète indisponible"
          description={s.error}
          action={
            <Button size="small" onClick={load}>
              Réessayer
            </Button>
          }
        />
      )}
      {d && !s.loading && (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space wrap>
            <ModelTag model={d.model} />
            {link ? (
              <Link to={link}>{d.label}</Link>
            ) : (
              <Text strong>{d.label}</Text>
            )}
            <StateTags item={d} />
            <Text type="secondary" copyable={{ text: d.documentId }}>
              id …{String(d.documentId).slice(-6)}
            </Text>
          </Space>
          <Space wrap>
            <Radio.Group
              size="small"
              value={mode}
              onChange={e => setMode(e.target.value)}
            >
              <Radio.Button value="changed">
                Champs modifiés ({changedCount})
              </Radio.Button>
              <Radio.Button value="all">
                Tous les champs ({fields.length})
              </Radio.Button>
            </Radio.Group>
            {changedCount === 0 && (
              <Text type="secondary">
                aucun champ modifié par la fusion sur cet enregistrement
              </Text>
            )}
          </Space>
          <Table
            size="small"
            pagination={false}
            dataSource={rows}
            columns={columns}
            scroll={{ x: true }}
            rowClassName={f =>
              f.changed ? "merge-row-changed" : "merge-row-unchanged"
            }
            onRow={f => ({
              style: f.changed ? { background: CHANGED_BG } : undefined,
            })}
            locale={{ emptyText: "Aucun champ à afficher." }}
          />
          {Array.isArray(d.rows) && d.rows.length > 0 && (
            <div>
              <Text type="secondary">Lignes de journal : </Text>
              {d.rows.map((r, i) => (
                <span key={r.seq}>
                  {i > 0 && <Text type="secondary">, </Text>}
                  {onOpenJournal ? (
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: "auto" }}
                      onClick={() => {
                        // close the card first: the journal opens behind it, on another tab
                        if (onClose) onClose();
                        onOpenJournal(r.seq);
                      }}
                      title={
                        r.appliedAt
                          ? `appliquée le ${fmtDate(r.appliedAt)}`
                          : undefined
                      }
                    >
                      {journalRowText(r)}
                    </Button>
                  ) : (
                    <Text>{journalRowText(r)}</Text>
                  )}
                  {r.undoneAt && (
                    <Tag color="purple" style={{ marginLeft: 4 }}>
                      annulée
                    </Tag>
                  )}
                </span>
              ))}
            </div>
          )}
        </Space>
      )}
    </Modal>
  );
}
